import React, { useEffect, useMemo, useState } from 'react';
import { EMPTY_RECORD, useDetailRecord } from 'helpers/detailRequest';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FaCheck, FaCheckCircle, FaHistory, FaShare, FaStream, FaSuitcaseRolling, FaTimesCircle } from 'react-icons/fa';
import {
  approveBooking, confirmBooking, getBooking, sendBackToAccountant, sendBackToHRAdmin,
} from 'redux/booking/travel/travelBookingSlice';
import useBreadcrumb from 'hooks/useBreadcrumb';
import { Card, Tabs, Alert, Button, DetailRecordFallback } from 'components/common';
import BookingDetailHeader from 'components/app/bookings/travel/booking/detail/BookingDetailHeader';
import BookingDetailInfo from 'components/app/bookings/travel/booking/detail/BookingDetailInfo';
import BookingScheduleItinerary from 'components/app/bookings/travel/booking/BookingScheduleItinerary';
import BookingJobTransactions from 'components/app/bookings/travel/booking/BookingJobTransactions';
import BookingActivity from 'components/app/bookings/travel/booking/detail/BookingActivity';
import BookingDetailRejectModal from 'components/app/bookings/travel/booking/detail/BookingDetailRejectModal';
import BookingDetailVerifyModal from 'components/app/bookings/travel/booking/detail/BookingDetailVerifyModal';

// Bootstrap css_class → Alert type
const ALERT_TYPE = { primary: 'info', secondary: 'info', success: 'success', warning: 'warning', danger: 'error', info: 'info' };

const TravelBookingDetailPage = () => {
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showVerifyForm, setShowVerifyForm] = useState(false);

  const params = useParams();
  // Only the record in the URL — the persisted store can still hold the one
  // viewed last (e.g. when opened from an email link).
  const { record, waiting, error } = useDetailRecord('booking_travel', params.id);
  const booking = record || EMPTY_RECORD;
  const location = useLocation();
  const dispatch = useDispatch();

  useBreadcrumb(
    booking
      ? [
          { label: 'Travel Booking', to: '/booking/travel/list' },
          { label: booking.code || booking.booking_code || `Travel ${params.id}` },
        ]
      : null,
  );

  const approvalType = new URLSearchParams(location.search).get('type');
  const bookingId = useMemo(() => booking?.id, [booking]);
  const { status, permissions } = booking || {};
  const {
    allow_approve, allow_reject, allow_send_to_accountant, allow_send_to_admin,
    allow_admin_hr_site_office_confirmation, allow_admin_hr_head_office_verification,
  } = permissions || {};

  const approve = () => dispatch(approveBooking(bookingId));

  // Email link (?type=approve|reject). Approve the booking in the URL: `booking` comes
  // from the persisted store and, until getBooking returns, may be empty or the one
  // viewed before — approving through it could approve the wrong booking.
  useEffect(() => {
    if (approvalType === null) return;
    if (approvalType === 'approve') dispatch(approveBooking(params.id));
    else setShowRejectForm(true);
  }, [approvalType, params.id, dispatch]);

  useEffect(() => {
    dispatch(getBooking(params.id));
  }, [params.id, dispatch]);

  const handleSendBackToAdminOrAccountant = () => {
    if (allow_send_to_accountant) dispatch(sendBackToAccountant(bookingId));
    else dispatch(sendBackToHRAdmin(bookingId));
  };

  const tabs = [
    { label: 'Detail', icon: <FaStream size={13} />, content: <BookingDetailInfo booking={booking} /> },
    { label: 'Schedule itinerary', icon: <FaSuitcaseRolling size={13} />, content: <BookingScheduleItinerary booking={booking} /> },
    { label: 'Job transactions', icon: <FaStream size={13} />, content: <BookingJobTransactions booking={booking} /> },
    { label: 'Activities', icon: <FaHistory size={13} />, content: <BookingActivity /> },
  ];

  if (!record) return <DetailRecordFallback waiting={waiting} title="Booking not available" error={error} onRetry={() => dispatch(getBooking(params.id))} />;

  return (
    <div className="tw-p-4">
      <BookingDetailHeader booking={booking} />

      <Alert type={ALERT_TYPE[status?.css_class] || 'info'} className="tw-mb-3">
        <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-2">
          <span>{booking?.workflow_message}</span>
          {(allow_send_to_accountant || allow_send_to_admin) && status?.id === 1 && (
            <Button variant="secondary" size="sm" leftIcon={FaShare} onClick={handleSendBackToAdminOrAccountant}>
              {allow_send_to_accountant ? 'Send back to Accountant now!' : 'Send back to HR Admin now!'}
            </Button>
          )}
          {allow_approve && <Button variant="success" size="sm" leftIcon={FaCheckCircle} onClick={approve}>Approve</Button>}
          {allow_reject && <Button variant="danger" size="sm" leftIcon={FaTimesCircle} onClick={() => setShowRejectForm(true)}>Reject</Button>}
          {allow_admin_hr_site_office_confirmation && <Button variant="success" size="sm" leftIcon={FaCheckCircle} onClick={() => dispatch(confirmBooking(bookingId))}>Confirm</Button>}
          {allow_admin_hr_head_office_verification && <Button variant="success" size="sm" leftIcon={FaCheck} onClick={() => setShowVerifyForm(true)}>Verify</Button>}
        </div>
      </Alert>

      <Card padding="none">
        <Tabs
          variant="underline"
          lazyMount
          navClassName="tw-px-3 tw-pt-2 tw-bg-white tw-flex-nowrap tw-overflow-x-auto tw-no-scrollbar"
          contentClassName="tw-bg-slate-50 tw-p-4"
          tabs={tabs}
        />
      </Card>

      {showRejectForm && <BookingDetailRejectModal showModal={showRejectForm} setshowModal={setShowRejectForm} booking={booking} />}
      {showVerifyForm && <BookingDetailVerifyModal showModal={showVerifyForm} setshowModal={setShowVerifyForm} booking={booking} />}
    </div>
  );
};

export default TravelBookingDetailPage;
